import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, Subscription } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import {
  AppleProfilePresetApiActions,
  AppleProfilePresetType,
  AppleProfilePresetViewActions,
  PresetModelType,
  selectPresetsByType,
  selectProfileTypeFromRoute,
} from '@era/stores';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { MasterComponent, MasterDetailComponent } from '@rocksoft/cdk';
import { filter, map, take } from 'rxjs/operators';
import { getHeaderLabelForProfileType, isValidProfileType } from './profile-preset-type-guard';
import { SettingsProfilePresetListComponent } from './settings-profile-preset-list/settings-profile-preset-list.component';

@Component({
  selector: 'ml-mdm-settings-profile-preset',
  templateUrl: './settings-profile-preset.container.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MasterDetailComponent, MasterComponent, SettingsProfilePresetListComponent, AsyncPipe],
})
export class SettingsProfilePresetContainerComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  profileType: AppleProfilePresetType;
  headerLabel = 'Name';

  private readonly subscriptions: Subscription[] = [];
  presets!: Observable<PresetModelType<AppleProfilePresetType>[]>;

  ngOnInit(): void {
    this.store
      .select(selectProfileTypeFromRoute)
      .pipe(take(1))
      .subscribe((profileType) => {
        if (!profileType || !isValidProfileType(profileType)) {
          throw new Error('profileType is required in route data and must be a valid AppleProfilePresetType');
        }
        this.profileType = profileType;
        this.headerLabel = getHeaderLabelForProfileType(this.profileType);
      });

    this.presets = this.store.select(selectPresetsByType(this.profileType));

    this.subscriptions.push(
      this.actions$
        .pipe(
          ofType(AppleProfilePresetApiActions.presetAdded),
          map((action) => {
            if (action.profileType === this.profileType) {
              return action.preset.id;
            }
            return null;
          }),
          filter((id) => id !== null)
        )
        .subscribe((id) => void this.router.navigate([id], { relativeTo: this.route }))
    );

    this.subscriptions.push(
      this.actions$
        .pipe(
          ofType(AppleProfilePresetApiActions.presetDeleted),
          filter((action) => action.profileType === this.profileType)
        )
        .subscribe(() => void this.router.navigate(['./'], { relativeTo: this.route }))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }

  public deletePreset(preset: PresetModelType<AppleProfilePresetType>) {
    if (!preset) {
      return;
    }

    this.store.dispatch(AppleProfilePresetViewActions.presetDeleted({ profileType: this.profileType, preset }));
  }
}
